import { useEffect, useState } from "react";
import { useSearch } from "./useSearch";
import { SearchResults } from "./SearchResults";
import { navigate, useCurrentRepo, useLocation } from "./router";

// SearchPage is the spec §10/§7 "Search" primary-nav page: a full-page
// search box over GET /api/repos/{repo}/search, results grouped by category
// (SearchResults). The query lives in the URL (?q=) so a search is
// shareable/bookmarkable, the same discipline the capability register
// established for its filter state — navigate(..., {replace:true}) so
// typing doesn't spam browser history. Search is repo-scoped to whichever
// repo the user is currently browsing (useCurrentRepo()) — search itself
// stays a global nav item (no /{repo} prefix on /search), but the results
// it returns are not, so this must know which repo to ask.
export function SearchPage({ token }: { token: string }) {
  const { search } = useLocation();
  const repo = useCurrentRepo();
  const [query, setQuery] = useState(() => new URLSearchParams(search).get("q") ?? "");
  const { results, status, message } = useSearch(token, repo, query);

  useEffect(() => {
    const fromURL = new URLSearchParams(search).get("q") ?? "";
    if (fromURL !== query) setQuery(fromURL);
    // Deliberately depends on `search` only: this re-syncs from the URL on
    // external navigation (e.g. a palette result linking to /search?q=...);
    // typing already drives both state and the URL together via onChange.
  }, [search]);

  function onChange(next: string) {
    setQuery(next);
    navigate(next.trim() ? `/search?q=${encodeURIComponent(next)}` : "/search", { replace: true });
  }

  return (
    <section className="panel">
      <h2>Search</h2>
      <input
        type="search"
        role="searchbox"
        aria-label="Search Cortex Atlas"
        className="search-box"
        placeholder="Search ADRs, capabilities, designs, runbooks, B&IT…"
        value={query}
        onChange={(e) => onChange(e.target.value)}
        autoFocus
      />

      {status === "unavailable" || status === "error" ? (
        <p className={status === "error" ? "err" : undefined}>{message}</p>
      ) : (
        <SearchResults results={results} />
      )}
    </section>
  );
}
